// src/Payroll_Management/PayRoll_Report/RemarksReportView.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ArrowLeft, Printer, User, Hash, Briefcase, Building2, FileText } from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

function s(v) {
  return (v ?? "").toString().trim();
}

const RemarksReportView = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // In our conversion, we pass employeeId (because ASP's usp_Get_Remarks expects EmployeeID)
  const employeeId = searchParams.get("employeeId") || "";

  const [loading, setLoading] = useState(false);
  const [row, setRow] = useState(null);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-46", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const fetchDetails = async () => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(employeeId)) {
      toast.error("employeeId missing in URL");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/performance-remarks-report/remark-details`, {
        params: {
          finYearId: Number(finYearId),
          employeeId: Number(employeeId),
        },
        timeout: 60000,
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to load remark details");
        return;
      }

      setRow(res.data.row || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = () => {
    // If you already created a print design like earlier pages, point it here:
    // Example:
    // window.open(`/PayRoll/RemarksViewPrint?employeeId=${employeeId}`, "_blank");
window.open(`/PayRoll/RemarksViewPrint?employeeId=${employeeId}`, "_blank");
  };

  const Item = ({ icon: Icon, label, value }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
          <Icon className={isDark ? "text-white" : "text-gray-700"} size={18} />
        </div>
        <div className={labelBase}>{label}</div>
      </div>
      <div className={`${isDark ? "text-gray-200" : "text-gray-900"} font-semibold`}>
        : {value || "-"}
      </div>
    </div>
  );

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VIEW REMARKS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-5xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* top row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                Employee ID: {employeeId || "-"}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => navigate(-1)} className={ghostBtn} disabled={loading}>
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <button onClick={handlePrint} className={primaryBtn} disabled={loading}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* body */}
          <div className={cardBase + " p-6"}>
            {loading ? (
              <div className="space-y-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-10 bg-gray-300 dark:bg-gray-700 rounded animate-pulse" />
                ))}
              </div>
            ) : !row ? (
              <div className={`py-10 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                No Records Found
              </div>
            ) : (
              <div className="space-y-5">
                <Item icon={Hash} label="Employee Code" value={row.fld_Employeecode} />
                <Item icon={User} label="Employee Name" value={row.fld_Employee} />
                <Item icon={Briefcase} label="Designation" value={row.fld_designationname} />
                <Item icon={Building2} label="Branch" value={row.fld_BranchName} />
                <Item icon={FileText} label="Title" value={row.fld_Title} />

                <div className="pt-3">
                  <div className={labelBase}>Details</div>
                  <textarea
                    className={`${inputBase} mt-2 min-h-[160px]`}
                    value={row.fld_Details || ""}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default RemarksReportView;
